<template>
    <vx-table-row>
        <vx-table-row-title>
            {{ model.email }}
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Team Role: </template>
            {{ TEAM_USER_ROLES[model.role] || '-' }}
        </vx-table-row-item>

        <vx-table-row-item v-if="props.hasContactGroups">
            <template v-slot:label> Target Access: </template>
            <div class="flex items-center gap-1">
                <vx-popover v-if="model.role !== 'none'" hover arrow placement="top">
                    <vx-badge size="xs" color="amber" data-test="corp-badge">
                        <font-awesome-icon :icon="faCrown" class="-mx-0.5"></font-awesome-icon>
                    </vx-badge>
                    <template v-slot:content> Corporate Access </template>
                </vx-popover>
                <vx-popover hover arrow placement="top">
                    <vx-badge size="xs" color="violet" data-test="ou-badge">
                        <font-awesome-icon :icon="faBuildingFlag"></font-awesome-icon>
                        {{ formatNumber(model.org_unit_invite_count) }}
                    </vx-badge>
                    <template v-slot:content> OU Invites </template>
                </vx-popover>
                <vx-popover hover arrow placement="top">
                    <vx-badge size="xs" color="slate" data-test="group-badge">
                        <font-awesome-icon :icon="faGrid2Plus" class="-mx-0.5"></font-awesome-icon>
                        {{ formatNumber(model.group_invite_count) }}
                    </vx-badge>
                    <template v-slot:content> Group Invites </template>
                </vx-popover>
            </div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            <div class="flex flex-wrap items-center gap-1">
                {{ model.status === 'pending' ? 'Pending' : 'Expired' }}
                <outside-verified-domains-badge
                    v-if="model.email_domain_verified === false && props.requireSso"
                    :email="model.email"
                />
            </div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Invited At: </template>
            <span :class="invitedAtColor" :title="shortDateTime(model.updated_at)" :key="invitedAtKey">
                {{ fromNow(model.updated_at) }}
            </span>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="props.isAdminOrOwner && !props.readOnly">
            <vx-dropdown placement="right">
                <vx-button
                    data-test="dropdown"
                    aria-label="Invitation actions"
                    type="button"
                    color="muted"
                    outline
                    class="w-[45px]"
                >
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item data-test="resend" @click="resendInvite()">
                        Re-send Invite
                        <font-awesome-icon widthAuto :icon="faPaperPlane"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item data-test="delete" @click="deleteInvite()">
                        Cancel Invite
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                v-if="props.hasContactGroups"
                :href="
                    props.isKiosk
                        ? `/kiosk/teams/${props.teamId}/invites/${model.id}/target-access`
                        : `/settings/teams/${props.teamId}/membership/invites/${model.id}/target-access`
                "
                data-test="edit"
                color="muted"
                outline
            >
                Edit <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import {
    faBuildingFlag,
    faCrown,
    faEllipsis,
    faFilePen,
    faGrid2Plus,
    faPaperPlane,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { formatNumber } from '~/utils/helpers';
import { computed, ref } from 'vue';
import { TEAM_USER_ROLES } from '~/constants/roles';
import dayjs from '~/utils/dayjs';
import { useInterval } from '@vueuse/core';
import { fromNow, shortDateTime } from '~/utils/date';
import { invitationsClient } from '~/services';
import { throwSwalRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import OutsideVerifiedDomainsBadge from '~/components/settings/teams/sso/OutsideVerifiedDomainsBadge.vue';

const toasted = useToasted();

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    isKiosk: {
        type: Boolean,
        default: false,
    },
    hasContactGroups: {
        type: Boolean,
        default: false,
    },
    isAdminOrOwner: {
        type: Boolean,
        default: false,
    },
    requireSso: {
        type: Boolean,
        default: false,
    },
});

const model = defineModel({
    type: Object,
    required: true,
});

const emit = defineEmits(['deleted', 'resent']);

const deleteInvite = (emitDeleted = true) => {
    return VxSwal.fire({
        title: 'Cancel Invite',
        text: 'Are you sure? There is no undo.',
        icon: 'danger',
        showCancelButton: true,
        confirmButtonColor: 'danger',
        confirmButtonText: 'Cancel Invite',
        cancelButtonText: 'Keep Invite',
        preConfirm: () =>
            invitationsClient
                .deleteInvite(props.teamId, model.value.id)
                .then(() => {
                    if (emitDeleted) {
                        emit('deleted', model.value.id);
                        toasted.success(`${model.value.email} invite canceled`);
                    }
                })
                .catch(throwSwalRequestError),
    });
};

const resending = ref(false);

const resendInvite = async () => {
    if (resending.value) {
        return;
    }

    return VxSwal.confirmation('Re-send Invite', 'Are you sure you want to re-send the invite?', 'success', () =>
        invitationsClient
            .resendInvite(props.teamId, model.value.id)
            .then((response) => {
                model.value = response.data;
                emit('resent', model.value);
                toasted.success('Invitation re-sent successfully');
            })
            .catch(throwSwalRequestError)
            .finally(() => {
                resending.value = false;
            })
    );
};

const invitedAtKey = useInterval(60000);
const invitedAtColor = computed(() => {
    if (model.value.status !== 'pending') {
        return 'text-rose-600';
    }

    const diffInDays = dayjs().diff(model.value.updated_at, 'days');

    if (diffInDays > 5) {
        return 'text-amber-600';
    }

    return 'text-emerald-700';
});
</script>
